import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FiZap, FiTarget, FiBox, FiLayers, FiGitBranch, FiGrid,
  FiList, FiFolder, FiActivity, FiBriefcase, FiTrendingUp,
  FiShield, FiCpu, FiServer, FiChevronDown, FiChevronRight,
  FiMenu, FiX, FiSearch
} from 'react-icons/fi';
import { useSolution } from '../../context/SolutionContext';
import SearchBar from '../ui/SearchBar';
import LayerAccordion from '../selector/LayerAccordion';
import { AI_ONTOLOGY } from '../../data/aiOntology';

const Sidebar = ({ activeView, setActiveView, isCollapsed, setIsCollapsed }) => {
  const { state } = useSolution();
  const [showLayers, setShowLayers] = useState(false);
  const [collapsedLayerGroups, setCollapsedLayerGroups] = useState(new Set());
  
  // ==================== VIEWS DEFINITION ====================
  // Grouped logically as requested
  
  const viewGroups = [
    {
      groupName: 'Planning & Design',
      icon: '🎯',
      views: [
        { id: 'auto', name: 'Auto-Architect', icon: FiZap },
        { id: 'alternatives', name: 'Alternatives', icon: FiCpu },
        { id: 'paradigms', name: 'AI Paradigms', icon: FiTrendingUp },
      ]
    },
    {
      groupName: 'Solution Views',
      icon: '📊',
      views: [
        { id: 'summary', name: 'Summary', icon: FiGrid },
        { id: 'list', name: 'List', icon: FiList },
        { id: 'folder', name: 'Folder', icon: FiFolder },
        { id: 'dendrograph', name: 'Dendrograph', icon: FiGitBranch },
        { id: 'architecture', name: 'Architecture', icon: FiLayers },
      ]
    },
    {
      groupName: 'Analysis & Traceability',
      icon: '🔍',
      views: [
        { id: 'matrix', name: 'Matrix', icon: FiGrid },
        { id: 'traceability', name: 'Traceability', icon: FiGitBranch },
        { id: 'approaches', name: '40 Approaches', icon: FiTarget },
        { id: 'validation', name: 'Validate', icon: FiShield },
      ]
    },
    {
      groupName: 'Execution & Ops',
      icon: '⚙️',
      views: [
        { id: 'functional', name: 'Functional', icon: FiActivity },
        { id: 'implementation', name: 'Implementation', icon: FiBriefcase },
        { id: 'bom', name: 'BOM', icon: FiBox },
        { id: 'transformation', name: 'Transformation', icon: FiTrendingUp },
        { id: 'wbs', name: 'WBS Plan', icon: FiBriefcase },
        { id: 'devops', name: 'DevOps & SRE', icon: FiServer },
      ]
    }
  ];

  // ==================== TOGGLE GROUP ====================
  
  const toggleLayerGroup = (groupId) => {
    setCollapsedLayerGroups(prev => {
      const newSet = new Set(prev);
      if (newSet.has(groupId)) newSet.delete(groupId);
      else newSet.add(groupId);
      return newSet;
    });
  };

  // ==================== FILTERED PHASES ====================
  
  const filteredPhases = AI_ONTOLOGY.phases.filter(phase => {
    if (!state.searchQuery) return true;
    const query = state.searchQuery.toLowerCase();
    return phase.name.toLowerCase().includes(query) ||
      phase.layers.some(layer =>
        layer.name.toLowerCase().includes(query) ||
        layer.subLayers.some(subLayer =>
          subLayer.name.toLowerCase().includes(query) ||
          subLayer.components.some(comp =>
            comp.name.toLowerCase().includes(query) ||
            comp.purpose?.toLowerCase().includes(query) ||
            comp.tags?.some(tag => tag.toLowerCase().includes(query))
          )
        )
      );
  });

  return (
    <motion.aside
      initial={{ x: -300, opacity: 0 }}
      animate={{ 
        x: 0, 
        opacity: 1,
        width: isCollapsed ? 64 : 340
      }}
      transition={{ duration: 0.3 }}
      className="bg-white border-r border-gray-200 flex flex-col h-full overflow-hidden shadow-sm"
    >
      {/* ==================== SIDEBAR HEADER ==================== */}
      <div className={`p-3 border-b border-gray-200 flex items-center ${isCollapsed ? 'justify-center' : 'justify-between'}`}>
        {!isCollapsed && (
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
              <span className="text-white text-sm font-bold">AI</span>
            </div>
            <span className="font-bold text-gray-900 text-sm">Navigation</span>
          </div>
        )}
        <button
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          title={isCollapsed ? 'Expand Sidebar' : 'Collapse Sidebar'}
        >
          {isCollapsed ? (
            <FiMenu className="w-4 h-4 text-gray-600" />
          ) : (
            <FiX className="w-4 h-4 text-gray-600" />
          )}
        </button>
      </div>

      {/* ==================== COLLAPSED STATE ICONS ==================== */}
      {isCollapsed ? (
        <div className="flex-1 overflow-y-auto py-3 space-y-1">
          {viewGroups.flatMap(group => group.views).map(view => (
            <button
              key={view.id}
              onClick={() => setActiveView(view.id)}
              className={`w-full flex items-center justify-center p-3 transition-colors ${
                activeView === view.id
                  ? 'bg-blue-500 text-white'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
              title={view.name}
            >
              <view.icon className="w-5 h-5" />
            </button>
          ))}
          
          <div className="border-t border-gray-200 my-2"></div>
          
          <button
            onClick={() => setShowLayers(!showLayers)}
            className={`w-full flex items-center justify-center p-3 transition-colors ${
              showLayers ? 'bg-purple-500 text-white' : 'text-gray-600 hover:bg-gray-100'
            }`}
            title="Layers"
          >
            <FiLayers className="w-5 h-5" />
          </button>
        </div>
      ) : (
        <>
          {/* ==================== EXPANDED STATE: VIEWS ==================== */}
          <div className="flex-1 overflow-y-auto">
            {/* Search Bar */}
            <div className="p-3 border-b border-gray-100">
              <SearchBar />
            </div>

            {/* View Groups */}
            <div className="p-3 space-y-4">
              {viewGroups.map((group, groupIndex) => (
                <div key={group.groupName}>
                  {/* Group Header */}
                  <div className="flex items-center gap-2 mb-2 px-1">
                    <span className="text-sm">{group.icon}</span>
                    <span className="text-xs font-bold text-gray-500 uppercase tracking-wide">
                      {group.groupName}
                    </span>
                  </div>
                  
                  {/* Group Views */}
                  <div className="space-y-1">
                    {group.views.map(view => (
                      <button
                        key={view.id}
                        onClick={() => setActiveView(view.id)}
                        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all text-sm font-medium ${
                          activeView === view.id
                            ? 'bg-blue-500 text-white shadow-md'
                            : 'text-gray-700 hover:bg-gray-100'
                        }`}
                      >
                        <view.icon className="w-4 h-4 flex-shrink-0" />
                        <span className="flex-1 text-left">{view.name}</span>
                        {activeView === view.id && (
                          <div className="w-1.5 h-1.5 bg-white rounded-full"></div>
                        )}
                      </button>
                    ))}
                  </div>
                  
                  {groupIndex < viewGroups.length - 1 && (
                    <div className="border-b border-gray-100 mt-3"></div>
                  )}
                </div>
              ))}
            </div>

            {/* ==================== LAYERS COLLAPSIBLE MENU ==================== */}
            <div className="border-t-2 border-gray-200 mt-2">
              <button
                onClick={() => setShowLayers(!showLayers)}
                className="w-full flex items-center justify-between p-4 hover:bg-purple-50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                    <FiLayers className="w-4 h-4 text-purple-600" />
                  </div>
                  <div className="text-left">
                    <div className="font-bold text-gray-900 text-sm">Layers</div>
                    <div className="text-xs text-gray-500">
                      {AI_ONTOLOGY.phases.length} phases • Browse components
                    </div>
                  </div>
                </div>
                {showLayers ? (
                  <FiChevronDown className="w-5 h-5 text-gray-500" />
                ) : (
                  <FiChevronRight className="w-5 h-5 text-gray-500" />
                )}
              </button>

              <AnimatePresence>
                {showLayers && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden bg-gray-50"
                  >
                    <div className="p-3 space-y-2 max-h-[60vh] overflow-y-auto">
                      {filteredPhases.length > 0 ? (
                        filteredPhases.map(phase => (
                          <LayerAccordion key={phase.id} phase={phase} />
                        ))
                      ) : (
                        <div className="text-center p-4 text-gray-500 text-sm">
                          No components found for "{state.searchQuery}"
                        </div>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </>
      )}
    </motion.aside>
  );
};

export default Sidebar;